import { useState, useEffect, useRef } from "react"
import { X, ChevronDown, Pencil, Bold, Italic, Underline, Link, Image, Code } from "lucide-react"
import { collection, getDocs, orderBy, query, where } from "firebase/firestore"
import { db } from "../services/firebase"
import { sendMessage } from "../services/messageService"
import { useUser } from "../contexts/UserContext"

const toast = {
  error: (msg) => console.error("Error:", msg),
  success: (msg) => console.log("Success:", msg),
}

const useEmailTemplates = () => {
  const [templates, setTemplates] = useState([])
  const { organizationId } = useUser()

  useEffect(() => {
    if (!organizationId) return
    const load = async () => {
      try {
        const q = query(
          collection(db, "emailTemplates"),
          where("organizationId", "==", organizationId)
        )
        const snap = await getDocs(q)
        setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      } catch (err) {
        console.error("Failed to load templates:", err)
      }
    }
    load()
  }, [organizationId])
  return templates
}

const useOutsideClick = (cb) => {
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb() }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [cb])
  return ref
}

// ── Toolbar ──────────────────────────────────────────────────────────────────

const ToolbarButton = ({ title, onClick, children, active }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); onClick?.() }}
    style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 28, height: 28, border: "none", borderRadius: 5,
      background: active ? "#f3f4f6" : "none",
      color: active ? "#111827" : "#374151",
      cursor: "pointer", flexShrink: 0, transition: "background 0.1s",
    }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#f3f4f6" }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "none" }}
  >
    {children}
  </button>
)

const ColorSwatch = ({ color, onSelect }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onSelect(color) }}
    style={{
      width: 16, height: 16, borderRadius: 3,
      background: color, border: "1px solid rgba(0,0,0,0.12)",
      cursor: "pointer", flexShrink: 0,
    }}
  />
)

const FONT_COLORS = ["#111827", "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#8b5cf6", "#ec4899"]

const EditorToolbar = ({ editorRef, isSending }) => {
  const [colorOpen, setColorOpen] = useState(false)
  const [activeFormats, setActiveFormats] = useState({})
  const colorRef = useOutsideClick(() => setColorOpen(false))

  useEffect(() => {
    const update = () => {
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      })
    }
    document.addEventListener("selectionchange", update)
    return () => document.removeEventListener("selectionchange", update)
  }, [])

  const exec = (cmd, val) => {
    editorRef.current?.focus()
    document.execCommand(cmd, false, val)
  }

  const handleLink = () => {
    const url = prompt("Enter URL:")
    if (url) exec("createLink", url.startsWith("http") ? url : `https://${url}`)
  }

  const handleImage = () => {
    const url = prompt("Enter image URL:")
    if (url) exec("insertImage", url)
  }

  if (isSending) return null

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 1,
      padding: "6px 14px",
      borderBottom: "1px solid #f3f4f6",
      background: "#fafafa",
      flexWrap: "wrap",
    }}>
      <ToolbarButton title="Bold (⌘B)" onClick={() => exec("bold")} active={activeFormats.bold}>
        <Bold size={13} strokeWidth={2.5} />
      </ToolbarButton>
      <ToolbarButton title="Italic (⌘I)" onClick={() => exec("italic")} active={activeFormats.italic}>
        <Italic size={13} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton title="Underline (⌘U)" onClick={() => exec("underline")} active={activeFormats.underline}>
        <Underline size={13} strokeWidth={2} />
      </ToolbarButton>

      {/* Font color */}
      <div ref={colorRef} style={{ position: "relative", display: "flex" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <ToolbarButton title="Font color" onClick={() => setColorOpen(v => !v)}>
            <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1, color: "#374151", fontFamily: "serif" }}>A</span>
              <span style={{ width: 12, height: 3, borderRadius: 1, background: "#111827" }} />
            </span>
          </ToolbarButton>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setColorOpen(v => !v) }}
            style={{
              width: 14, height: 28, border: "none", background: "none",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
            }}
          >
            <ChevronDown size={9} strokeWidth={2.5} color="#9ca3af" />
          </button>
        </div>
        {colorOpen && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            padding: "8px", zIndex: 600,
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4,
          }}>
            {FONT_COLORS.map(c => (
              <ColorSwatch key={c} color={c} onSelect={(col) => { exec("foreColor", col); setColorOpen(false) }} />
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px", flexShrink: 0 }} />

      <ToolbarButton title="Link" onClick={handleLink}>
        <Link size={13} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton title="Image" onClick={handleImage}>
        <Image size={13} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton title="Code" onClick={() => exec("formatBlock", "pre")}>
        <Code size={13} strokeWidth={2} />
      </ToolbarButton>

      {/* Divider */}
      <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px", flexShrink: 0 }} />

      {/* More */}
      <ToolbarButton title="More options" onClick={() => {}}>
        <span style={{ display: "flex", gap: 2 }}>
          {[0,1,2].map(i => <span key={i} style={{ width: 3, height: 3, borderRadius: "50%", background: "#6b7280", display: "block" }} />)}
        </span>
      </ToolbarButton>
    </div>
  )
}

const resolvePlaceholders = (template, ctx) => {
  if (!ctx) return template
  return template.replace(/\{\{([^}]+)\}\}/g, (_, path) => {
    const value = path.split(".").reduce((obj, key) => obj?.[key], ctx)
    return value ?? `{{${path}}}`
  })
}

// ── Modal ─────────────────────────────────────────────────────────────────────
const EmailModal = ({
  isOpen = true,
  onClose = () => {},
  organizationId = "",
  applicantId = "",
  senderId = "",
  candidateName = "",
  candidateEmail = "",
  confirmLabel = "Send Email",
  onSendComplete = null,
  context = null,
}) => {
  const templates = useEmailTemplates()
  const DOMAIN = "@resolut.co.in"
  const DEFAULT_PREFIX = "communications"
  const [isEditingFrom, setIsEditingFrom] = useState(false)
  const [fromPrefix, setFromPrefix] = useState(DEFAULT_PREFIX)
  const [fromDraft, setFromDraft] = useState(DEFAULT_PREFIX)
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [excludeActive, setExcludeActive] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [visible, setVisible] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)

  const editorRef = useRef(null)
  const subjectRef = useRef(null)
  const fromInputRef = useRef(null)
  const templateRef = useOutsideClick(() => setTemplateOpen(false))

  useEffect(() => {
    if (isOpen) {
      const t = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(t)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) setTimeout(() => subjectRef.current?.focus(), 120)
  }, [isOpen])

  useEffect(() => {
    if (isEditingFrom) setTimeout(() => fromInputRef.current?.focus(), 50)
  }, [isEditingFrom])

  const getTextContent = (html) => {
    const div = document.createElement("div")
    div.innerHTML = html
    return div.textContent || div.innerText || ""
  }

  const canSend =
    !isSending &&
    subject.trim().length > 0 &&
    getTextContent(message).trim().length > 0

  useEffect(() => {
    const onKey = (e) => {
      if (!isOpen) return
      if (e.key === "Escape" && !isSending) {
        if (isEditingFrom) {
          setIsEditingFrom(false)
          setFromDraft(fromPrefix)
          return
        }
        handleClose()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault()
        if (canSend) handleSend()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isOpen, isSending, canSend, isEditingFrom, fromPrefix])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 200)
  }

  const handleFromSave = () => {
    const trimmed = fromDraft.trim().replace(/[^a-zA-Z0-9._+-]/g, "")
    if (trimmed) setFromPrefix(trimmed)
    else setFromDraft(fromPrefix)
    setIsEditingFrom(false)
  }

  const handleSelectTemplate = (tpl) => {
    const subject = tpl.subject ? resolvePlaceholders(tpl.subject, context) : ""
    const body = tpl.body
      ? resolvePlaceholders(tpl.body.replace(/\n/g, "<br>"), context)
      : ""

    if (subject) setSubject(subject)
    if (body && editorRef.current) {
      editorRef.current.innerHTML = body
      setMessage(body)
    }

    setTemplateOpen(false)
    setTimeout(() => editorRef.current?.focus(), 50)
  }

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error("Please enter a subject")
      return
    }
    if (!getTextContent(message).trim()) {
      toast.error("Please enter a message")
      return
    }
    setIsSending(true)
    try {
      await sendMessage(organizationId, applicantId, message.trim(), senderId)
      toast.success("Message sent!")
      setSubject("")
      setMessage("")
      if (editorRef.current) editorRef.current.innerHTML = ""
      if (onSendComplete) {
        onSendComplete() // caller handles close + archive
      } else {
        handleClose()
      }
    } catch (err) {
      console.error(err)
      toast.error("Failed to send.")
    } finally {
      setIsSending(false)
    }
  }

  if (!isOpen) return null

  const ff =
    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  const divider = "1px solid #f3f4f6"

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        .em-editor { font-family: ${ff}; }
        .em-editor:empty::before { content: attr(data-placeholder); color: #9ca3af; pointer-events: none; }
        .em-editor:focus { outline: none; }
        .em-editor p { margin: 0.35em 0; }
        .em-editor ul { margin: 0.4em 0; padding-left: 1.4em; list-style: disc; }
        .em-editor ol { margin: 0.4em 0; padding-left: 1.4em; list-style: decimal; }
        .em-editor li { display: list-item; margin: 0.15em 0; }
        .em-tpl-btn:hover { background: #f3f4f6 !important; }
        .em-close-btn:hover { color: #374151 !important; background: #f3f4f6 !important; }
        .em-from-edit:hover { background: #f3f4f6 !important; }
        @keyframes em-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={!isSending ? handleClose : undefined}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200,
          background: "rgba(0,0,0,0.28)",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.18s ease",
        }}
      />

      {/* Centering wrapper */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 201,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
          pointerEvents: "none",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 620,
            background: "#fff",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            fontFamily: ff,
            pointerEvents: "all",
            opacity: visible ? 1 : 0,
            transform: visible
              ? "translateY(0) scale(1)"
              : "translateY(6px) scale(0.99)",
            transition:
              "opacity 0.18s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1)",
            overflow: "hidden",
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderBottom: divider,
              background: "#fff",
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#111827",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Send Email
              </p>
              {candidateName && (
                <p
                  style={{
                    fontSize: 13,
                    color: "#374151",
                    margin: "3px 0 0",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  to{" "}
                  <span style={{ fontWeight: 600, color: "#111827" }}>
                    {candidateName}
                  </span>
                  {candidateEmail && (
                    <>
                      <span style={{ color: "#d1d5db" }}>·</span>
                      <span style={{ color: "#6b7280", fontWeight: 500 }}>
                        {candidateEmail}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              disabled={isSending}
              className="em-close-btn"
              style={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "none",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                color: "#6b7280",
                transition: "all 0.12s",
              }}
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>

          {/* ── Exclude checkbox ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 20px",
              borderBottom: divider,
            }}
          >
            <input
              type="checkbox"
              id="em-excl"
              checked={excludeActive}
              onChange={(e) => setExcludeActive(e.target.checked)}
              style={{
                width: 14,
                height: 14,
                cursor: "pointer",
                accentColor: "#111827",
                flexShrink: 0,
              }}
            />
            <label
              htmlFor="em-excl"
              style={{
                fontSize: 13,
                color: "#374151",
                cursor: "pointer",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 5,
                userSelect: "none",
              }}
            >
              Don't email candidates with other active opportunities
              <span
                title="Candidates with open opportunities in other pipelines will be skipped"
                style={{
                  width: 15,
                  height: 15,
                  border: "1.5px solid #9ca3af",
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  color: "#6b7280",
                  cursor: "help",
                  flexShrink: 0,
                  fontWeight: 600,
                }}
              >
                i
              </span>
            </label>
          </div>

          {/* ── From row ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 20px",
              height: 46,
              borderBottom: divider,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                width: 76,
                flexShrink: 0,
              }}
            >
              From
            </span>

            <div
              style={{ flex: 1, display: "flex", alignItems: "center", gap: 0 }}
            >
              {isEditingFrom ? (
                <input
                  ref={fromInputRef}
                  value={fromDraft}
                  onChange={(e) =>
                    setFromDraft(
                      e.target.value.replace(/[^a-zA-Z0-9._+-]/g, ""),
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleFromSave()
                    if (e.key === "Escape") {
                      setIsEditingFrom(false)
                      setFromDraft(fromPrefix)
                    }
                  }}
                  onBlur={handleFromSave}
                  style={{
                    border: "none",
                    outline: "none",
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#111827",
                    background: "transparent",
                    fontFamily: ff,
                    padding: 0,
                    minWidth: 0,
                    width: `${Math.max(fromDraft.length, 4)}ch`,
                  }}
                />
              ) : (
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}
                >
                  {fromPrefix}
                </span>
              )}
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#6b7280",
                  flexShrink: 0,
                }}
              >
                {DOMAIN}
              </span>
            </div>

            <button
              onClick={() => {
                setFromDraft(fromPrefix)
                setIsEditingFrom(true)
              }}
              className="em-from-edit"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 8px",
                flexShrink: 0,
                background: "none",
                border: "none",
                borderRadius: 5,
                fontSize: 12.5,
                fontWeight: 600,
                color: "#6b7280",
                cursor: "pointer",
                fontFamily: ff,
                transition: "all 0.12s",
              }}
            >
              <Pencil size={11} strokeWidth={2} />
              Edit
            </button>
          </div>

          {/* ── Subject row ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 20px",
              height: 46,
              borderBottom: divider,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                width: 76,
                flexShrink: 0,
              }}
            >
              Subject
            </span>
            <input
              ref={subjectRef}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Write a subject…"
              disabled={isSending}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: 14,
                fontWeight: 500,
                color: "#111827",
                background: "transparent",
                fontFamily: ff,
                padding: "0",
              }}
            />
          </div>

          {/* ── Toolbar (below subject) ── */}
          <EditorToolbar editorRef={editorRef} isSending={isSending} />

          {/* ── Editor ── */}
          <div
            ref={editorRef}
            contentEditable={!isSending}
            onInput={() => {
              if (editorRef.current) setMessage(editorRef.current.innerHTML)
            }}
            className="em-editor"
            data-placeholder="Write your message…"
            suppressContentEditableWarning
            style={{
              minHeight: 330,
              padding: "16px 20px",
              fontSize: 14,
              lineHeight: 1.75,
              color: "#111827",
              fontFamily: ff,
            }}
          />

          {/* ── Footer ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              borderTop: divider,
              background: "#fff",
            }}
          >
            {/* Template dropdown */}
            <div ref={templateRef} style={{ position: "relative" }}>
              <button
                type="button"
                className="em-tpl-btn"
                onMouseDown={(e) => {
                  e.preventDefault()
                  setTemplateOpen((v) => !v)
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  height: 32,
                  padding: "0 11px",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  background: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#374151",
                  cursor: "pointer",
                  fontFamily: ff,
                  transition: "background 0.12s",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <rect
                    x="1"
                    y="1"
                    width="6"
                    height="6"
                    rx="1.5"
                    fill="#6b7280"
                  />
                  <rect
                    x="9"
                    y="1"
                    width="6"
                    height="2.5"
                    rx="1"
                    fill="#6b7280"
                  />
                  <rect
                    x="9"
                    y="5.5"
                    width="6"
                    height="1.5"
                    rx="0.75"
                    fill="#9ca3af"
                  />
                  <rect
                    x="1"
                    y="9"
                    width="14"
                    height="2"
                    rx="1"
                    fill="#9ca3af"
                  />
                  <rect
                    x="1"
                    y="13"
                    width="9"
                    height="2"
                    rx="1"
                    fill="#9ca3af"
                  />
                </svg>
                Template
                <ChevronDown
                  size={10}
                  strokeWidth={2.5}
                  style={{
                    marginLeft: 1,
                    transform: templateOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.15s",
                  }}
                />
              </button>

              {templateOpen && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 6px)",
                    left: 0,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    boxShadow:
                      "0 -4px 20px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.06)",
                    zIndex: 500,
                    minWidth: 200,
                    overflow: "hidden",
                  }}
                >
                  {templates.length === 0 ? (
                    <div
                      style={{
                        padding: "10px 14px",
                        fontSize: 13.5,
                        color: "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      No templates yet
                    </div>
                  ) : (
                    templates.map((t, i) => (
                      <button
                        key={t.id ?? i}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleSelectTemplate(t)
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          padding: "9px 14px",
                          border: "none",
                          borderBottom: "1px solid #f9fafb",
                          background: "none",
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: "#111827",
                          cursor: "pointer",
                          fontFamily: ff,
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f9fafb")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "none")
                        }
                      >
                        {t.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                onClick={handleClose}
                disabled={isSending}
                style={{
                  background: "none",
                  border: "1px solid #e5e7eb",
                  borderRadius: 7,
                  padding: "7px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#374151",
                  cursor: "pointer",
                  fontFamily: ff,
                  transition: "all 0.12s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#9ca3af"
                  e.currentTarget.style.color = "#111827"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb"
                  e.currentTarget.style.color = "#374151"
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleSend}
                disabled={!canSend}
                style={{
                  height: 36,
                  padding: "0 20px",
                  background: canSend ? "#111827" : "#f3f4f6",
                  border: "none",
                  borderRadius: 7,
                  fontSize: 13,
                  fontWeight: 600,
                  color: canSend ? "#fff" : "#9ca3af",
                  cursor: canSend ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: ff,
                  letterSpacing: "-0.01em",
                  transition: "background 0.12s, color 0.12s",
                }}
                onMouseEnter={(e) => {
                  if (canSend) e.currentTarget.style.background = "#27272a"
                }}
                onMouseLeave={(e) => {
                  if (canSend) e.currentTarget.style.background = "#111827"
                }}
              >
                {isSending ? (
                  <>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        border: "1.5px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "em-spin 0.7s linear infinite",
                      }}
                    />
                    Sending…
                  </>
                ) : (
                  confirmLabel
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default EmailModal